import { Elements } from '@stripe/react-stripe-js';
import get from 'lodash.get';
import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { AiFillBank, AiOutlineUser } from 'react-icons/ai';
import { GiModernCity } from 'react-icons/gi';
import { IoMdCard } from 'react-icons/io';
import { IconType } from 'react-icons/lib';
import { RiBookLine } from 'react-icons/ri';
import { toast } from 'react-toastify';

import apiUtil from 'src/api/businessOnboarding';
import CustomToast from 'src/components/CustomToast';
import AreYouSureModal from 'src/components/ExternalConfirmationModal copy';
import Loader from 'src/components/Loader';
import SubNav from 'src/components/SubNav';
import { stripeContext } from 'src/containers/BusinessOnboarding/BankDetails/util';
import { ONBOARDING_START_ERROR, ONBOARDING_UPDATE_ERROR, SOMETHING_WENT_WRONG } from 'src/containers/Core/Messages';
import { AppContext } from '../Core/AppContext';
import { AppState, setBusinessOnboardingDetails } from '../Core/AppReducer';
import Activation from './Activation';
import BankDetails from './BankDetails/BankDetails';
import JoinCommunitySubmit from './Components/JoinCommunitySubmit';
import CreateBusinessProfile from './CreateBusinessProfile';
import ExistingBusinesses from './ExistingBusinesses';
import JoinCommunity from './JoinCommunity';
import MatchingBusinesses from './MatchingBusinesses';
import RewardTutorial from './RewardTutorial';

const BusinessOnboardingFlow = () => {
  const { store, dispatch } = useContext(AppContext);
  const { businessOnboardingDetails }: AppState = store;
  const [isLoading, setIsLoading] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [businessUuid, setBusinessUuid] = useState<string>(get(businessOnboardingDetails, 'business.uuid'));

  const step: BUSINESS_ONBOARDING_STEP = businessOnboardingDetails && businessOnboardingDetails.step;
  const state: OnboardingStepProcess = businessOnboardingDetails && businessOnboardingDetails.state;

  useEffect(() => {
    setBusinessUuid(get(businessOnboardingDetails, 'business.uuid'));
  }, [businessOnboardingDetails]);

  const startBusinessOnboarding = async () => {
    try {
      setIsLoading(true);
      const response = await apiUtil.createBusinessOnboarding({
        state: OnboardingStepProcess.IN,
        step: BUSINESS_ONBOARDING_STEPS_MAP[BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_OWNED],
      });
      dispatch(setBusinessOnboardingDetails(response));
    } catch {
      toast(<CustomToast title={SOMETHING_WENT_WRONG} description={ONBOARDING_START_ERROR} />);
    } finally {
      setIsLoading(false);
    }
  };

  const updateBusinessOnboarding = async (
    nextStep: BUSINESS_ONBOARDING_STEP,
    nextState?: OnboardingStepProcess,
    businessId?: string
  ) => {
    try {
      setIsLoading(true);
      const response = await apiUtil.updateBusinessOnboarding(businessOnboardingDetails.uuid, {
        state: nextState || OnboardingStepProcess.IN,
        step: BUSINESS_ONBOARDING_STEPS_MAP[nextStep],
        ...(businessId || businessUuid ? { businessUuid: businessId || businessUuid } : {}),
      });
      dispatch(setBusinessOnboardingDetails(response));
    } catch {
      toast(<CustomToast title={SOMETHING_WENT_WRONG} description={ONBOARDING_UPDATE_ERROR} />);
    } finally {
      setIsLoading(false);
    }
  };

  const onExitConfirmation = async () => {
    await updateBusinessOnboarding(BUSINESS_ONBOARDING_STEP.EXIT, OnboardingStepProcess.EX);
    setShowExitConfirmation(false);
    await startBusinessOnboarding();
  };

  const getStepTitleAndContent = () => {
    let stepContent;
    let stepTitle;

    switch (step) {
      case BUSINESS_ONBOARDING_STEP.EXIT:
        stepTitle = 'Business Profile';
        stepContent = null;
        break;
      case BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_OWNED:
        stepTitle = 'Business Profile';
        stepContent = (
          <ExistingBusinesses
            setOnboardingStep={(stp: BUSINESS_ONBOARDING_STEP, businessId?: string) =>
              updateBusinessOnboarding(stp, OnboardingStepProcess.IN, businessId)
            }
          />
        );
        break;
      case BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_FORM:
        stepTitle = 'Business Profile';
        stepContent = (
          <CreateBusinessProfile
            setOnboardingStep={(stp: BUSINESS_ONBOARDING_STEP, businessId?: string) => {
              updateBusinessOnboarding(stp, OnboardingStepProcess.IN, businessId);
            }}
          />
        );
        break;
      case BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_MATCHING:
        stepTitle = 'Business Profile';
        stepContent = (
          <MatchingBusinesses
            setOnboardingStep={(stp: BUSINESS_ONBOARDING_STEP, businessId?: string) =>
              updateBusinessOnboarding(stp, OnboardingStepProcess.IN, businessId)
            }
          />
        );
        break;
      case BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_TUTORIAL:
        stepTitle = 'How Open Rewards Works';
        stepContent = (
          <RewardTutorial setOnboardingStep={(stp: BUSINESS_ONBOARDING_STEP) => updateBusinessOnboarding(stp)} />
        );
        break;
      case BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_ADD_BANK:
        stepTitle = 'Connect Your Payment';
        stepContent = (
          <Elements stripe={stripeContext()}>
            <BankDetails setOnboardingStep={(stp: BUSINESS_ONBOARDING_STEP) => updateBusinessOnboarding(stp)} />
          </Elements>
        );
        break;
      case BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_ACTIVATION:
        stepTitle = 'Activation';
        stepContent = (
          <Activation setOnboardingStep={(stp: BUSINESS_ONBOARDING_STEP) => updateBusinessOnboarding(stp)} />
        );
        break;
      case BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_COMMUNITY:
      case BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_WAITLIST:
        stepTitle = 'Join Community';
        if (state === OnboardingStepProcess.IN) {
          stepContent = (
            <JoinCommunity
              setOnboardingStep={(stp: BUSINESS_ONBOARDING_STEP, stat: OnboardingStepProcess) =>
                updateBusinessOnboarding(stp, stat)
              }
              onExit={() => setShowExitConfirmation(true)}
            />
          );
        } else {
          stepContent = <JoinCommunitySubmit />;
        }
        break;
      case BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_COMPLETE:
        stepTitle = 'Join Community';
        stepContent = <JoinCommunitySubmit />;
        break;

      default:
        stepTitle = null;
        stepContent = null;
        break;
    }

    return {
      stepTitle,
      stepContent,
    };
  };

  const renderQuitButton = () => {
    if (
      [
        BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_TUTORIAL,
        BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_ACTIVATION,
        BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_ADD_BANK,
      ].includes(step)
    ) {
      return (
        <Button
          className="px-2 mb-3 mr-3"
          size="sm"
          variant="outline-primary"
          onClick={() => setShowExitConfirmation(true)}
          style={{ position: 'absolute', bottom: 0, right: 0 }}
        >
          Quit
        </Button>
      );
    }
  };

  const renderOnboardingSteps = () => {
    const { stepTitle, stepContent }: { stepTitle: string; stepContent: React.ReactNode } = getStepTitleAndContent();

    return (
      <div className="h-100">
        <AreYouSureModal
          yes="Yes, Quit"
          no="No, Cancel"
          confirmationMessage="Are you sure you wish to quit the on-boarding flow? All data would be deleted."
          onConfirmClick={() => {
            onExitConfirmation();
          }}
          onHide={() => setShowExitConfirmation(false)}
          show={showExitConfirmation}
        />
        <SubNav left={stepTitle} className="font-weight-bold" />
        {isLoading ? <Loader /> : stepContent}
        {renderQuitButton()}
      </div>
    );
  };

  return <div className="h-100">{renderOnboardingSteps()}</div>;
};

export default BusinessOnboardingFlow;

export const OnboardingStepIconWrapper = (Icon: IconType) => (
  <span className="rounded-circle onboarding-step-icon d-inline-block">
    <Icon size={32} className="p-2" />
  </span>
);

export enum BUSINESS_ONBOARDING_STEP {
  EXIT = 'EXIT',
  START = 'START',
  ONBOARD_BUSINESS_OWNED = 'ONBOARD_BUSINESS_OWNED',
  ONBOARD_BUSINESS_FORM = 'ONBOARD_BUSINESS_FORM',
  ONBOARD_BUSINESS_MATCHING = 'ONBOARD_BUSINESS_MATCHING',
  ONBOARD_BUSINESS_TUTORIAL = 'ONBOARD_BUSINESS_TUTORIAL',
  ONBOARD_BUSINESS_ADD_BANK = 'ONBOARD_BUSINESS_ADD_BANK',
  ONBOARD_BUSINESS_ACTIVATION = 'ONBOARD_BUSINESS_TOPUP',
  ONBOARD_BUSINESS_COMMUNITY = 'ONBOARD_BUSINESS_COMMUNITY',
  ONBOARD_BUSINESS_WAITLIST = 'ONBOARD_BUSINESS_WAITLIST',
  ONBOARD_BUSINESS_COMPLETE = 'ONBOARD_BUSINESS_COMPLETE',
}

export enum OnboardingStepProcess {
  IN = 'IN',
  CL = 'CL',
  PN = 'PN',
  EX = 'EX',
}

export const BUSINESS_ONBOARDING_STEPS_MAP = {
  [BUSINESS_ONBOARDING_STEP.EXIT]: '-1',
  [BUSINESS_ONBOARDING_STEP.START]: '0',
  [BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_OWNED]: '1',
  [BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_FORM]: '2',
  [BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_MATCHING]: '3',
  [BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_TUTORIAL]: '4',
  [BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_ADD_BANK]: '5',
  [BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_ACTIVATION]: '6',
  [BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_COMMUNITY]: '7',
  [BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_WAITLIST]: '8',
  [BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_COMPLETE]: '9',
};

export enum ONBOARDING_STEP_UI {
  BUSINESS_PROFILE = 'Business Profile',
  REWARD_TUTORIAL = 'How Open Rewards Works',
  SETUP_BANK_ACCOUNT = 'Connect Your Payment',
  ACTIVATION = 'Activation',
  JOIN_COMMUNITY = 'Join Community',
}

export const LEFT_MENU_ONBOARDING_STEPS = [
  ONBOARDING_STEP_UI.BUSINESS_PROFILE,
  ONBOARDING_STEP_UI.REWARD_TUTORIAL,
  ONBOARDING_STEP_UI.SETUP_BANK_ACCOUNT,
  ONBOARDING_STEP_UI.ACTIVATION,
  ONBOARDING_STEP_UI.JOIN_COMMUNITY,
];

export const OnboardingStepIconMap: { [key: string]: React.ReactElement } = {
  [ONBOARDING_STEP_UI.BUSINESS_PROFILE]: OnboardingStepIconWrapper(AiOutlineUser),
  [ONBOARDING_STEP_UI.REWARD_TUTORIAL]: OnboardingStepIconWrapper(RiBookLine),
  [ONBOARDING_STEP_UI.SETUP_BANK_ACCOUNT]: OnboardingStepIconWrapper(AiFillBank),
  [ONBOARDING_STEP_UI.ACTIVATION]: OnboardingStepIconWrapper(IoMdCard),
  [ONBOARDING_STEP_UI.JOIN_COMMUNITY]: OnboardingStepIconWrapper(GiModernCity),
};
